/**
 * GENERATED by /docs-writer from docs/request-access.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/request-access.md`.
 *
 * source-sha: 22c38add90073a6ab135b7f0e5001f17d7e84421
 * sources (from the markdown's "Sources" section):
 *   - The form, its fields, prefill links: apps/web/src/pages/TaskNewPage.tsx (access template), apps/web/src/pages/ClusterDetailPage.tsx, apps/web/src/pages/RepoDetailPage.tsx, apps/web/src/pages/SecretDetailPage.tsx
 *   - Systems, providers, groups, durations, the 120-character limit: packages/shared-types/src/access-requests.ts
 *   - Creating, default assignees, due date as expiry, visibility, comments, mentions, cancel, close, edit the reason: packages/inventory-core/src/tasks/crud.ts, packages/inventory-core/src/tasks/access-grant.ts, packages/inventory-core/src/tasks/visibility.ts
 *   - Approve, deny, who may act: packages/inventory-core/src/tasks/access-grant.ts, packages/shared-types/src/rbac.ts, apps/api/src/app/tasks/tasks.resolver.ts
 *   - Email, Slack and browser notifications: apps/api/src/app/tasks/task-effects.service.ts, apps/api/src/app/tasks/email-notify.service.ts, apps/api/src/app/tasks/slack-notify.service.ts, packages/inventory-core/src/push.ts, apps/web/src/components/NotificationCenter.tsx
 *   - The list, the task page, the default assignees page: apps/web/src/pages/TasksPage.tsx, apps/web/src/pages/TaskDetailPage.tsx, apps/web/src/pages/AccessReviewersPage.tsx, apps/api/src/app/tasks/access-admin.resolver.ts
 *   - The six MCP tools: packages/shared-types/src/mcp.ts, apps/mcp/src/handlers.ts
 *   - Engineers get the Developer role on GitLab repositories by default: conversation, 2026-10-10 (repo owner)
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';
import { ReviewerRoster } from '../components/docs/live/ReviewerRoster';

const TOC = [
  { id: 'create-an-access-task', label: 'Create an access task' },
  { id: 'which-system-to-pick', label: 'Which system to pick' },
  { id: 'how-long-the-access-lasts', label: 'How long the access lasts' },
  { id: 'what-happens-after-you-create-it', label: 'What happens after you create it' },
  { id: 'who-decides', label: 'Who decides' },
  { id: 'follow-your-task', label: 'Follow your task' },
  { id: 'from-an-ai-agent', label: 'From an AI agent' },
];

export function RequestAccessDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Access · Guide</p>
        <h1 className="doc-title">Requesting access</h1>
        <p className="doc-lede">
          To get access to a company system, create an <strong>access task</strong> on{' '}
          <Link to="/tasks">Tasks</Link>. <strong>Anyone signed in can create one</strong>, including a new
          member who has no role yet. An access task is an ordinary task with an access grant on it, so
          everything in <Link to="/docs/tasks">Tasks</Link> applies: assignees, labels, comments, the due
          date, the board. This page covers only what is specific to access.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-19">19 Oct 2026</time>
        </p>

        <p className="doc-p">
          The default access assignees are assigned as soon as you create it, and the task ends as{' '}
          <strong>approved</strong>, <strong>denied</strong> or <strong>cancelled</strong>.
        </p>

        <h2 className="doc-h2" id="create-an-access-task">Create an access task</h2>
        <p className="doc-p">The form asks four questions. Only the reason has no default.</p>
        <ol className="doc-list">
          <li>
            Open <Link to="/tasks">Tasks</Link>, press <strong>+ New task</strong> and pick the{' '}
            <strong>Access</strong> template.
          </li>
          <li>
            Under <strong>Which system?</strong>, pick one of the twelve systems. Each option names the
            provider behind it.
          </li>
          <li>
            Under <strong>Which one?</strong>, name the exact target, such as a database name, a Grafana host
            or a GCP project id. The field is optional and holds at most <strong>120 characters</strong>. Left
            blank, the task is titled with the system&rsquo;s name.
          </li>
          <li>
            Under <strong>Why do you need it?</strong>, say what you are doing and which resource you need.
            The form does not submit without a reason.
          </li>
          <li>
            Under <strong>Access until</strong>, pick the last day you need the access, or leave it empty for
            permanent access.
          </li>
          <li>Press <strong>Create task</strong>. The page of your new task opens.</li>
        </ol>
        <p className="doc-p">
          Beside those fields you can also set a title (left blank it reads "Access: <em>system</em>{' '}
          "<em>target</em>"", and the form shows it), add <strong>Assignees</strong> and add labels. The{' '}
          <strong>Assignees</strong> field opens with the default access assignees already in it, as chips you can
          remove. Add anyone else you want as an approver, whatever their role. A new member can do the same; only
          the labels are closed to a new member.
        </p>
        <p className="doc-p">
          A cluster page, a repository page and a secret page in the inventory each link to this form with the
          system and the target filled in. A task created that way is linked to the inventory entry, and the
          form locks both fields.
        </p>

        <h2 className="doc-h2" id="which-system-to-pick">Which system to pick</h2>
        <p className="doc-p">
          The list has twelve systems in five groups. Pick <strong>Other</strong> when none of the first
          eleven fits, and name the system in the reason.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>System</th>
                <th>Provider</th>
                <th>Group in the list</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Database</strong></td>
                <td>MongoDB Atlas / DO Postgres</td>
                <td>Data &amp; secrets</td>
              </tr>
              <tr>
                <td><strong>Secret / credentials</strong></td>
                <td>GCP Secret Manager</td>
                <td>Data &amp; secrets</td>
              </tr>
              <tr>
                <td><strong>Kubernetes cluster</strong></td>
                <td>DigitalOcean Kubernetes</td>
                <td>Infrastructure</td>
              </tr>
              <tr>
                <td><strong>Server / droplet</strong></td>
                <td>DigitalOcean</td>
                <td>Infrastructure</td>
              </tr>
              <tr>
                <td><strong>Code repository</strong></td>
                <td>GitLab</td>
                <td>Code &amp; CI</td>
              </tr>
              <tr>
                <td><strong>AWS account</strong></td>
                <td>AWS</td>
                <td>Cloud &amp; network</td>
              </tr>
              <tr>
                <td><strong>Cloudflare</strong></td>
                <td>Cloudflare</td>
                <td>Cloud &amp; network</td>
              </tr>
              <tr>
                <td><strong>VPN / SecureLink</strong></td>
                <td>WireGuard / SecureLink</td>
                <td>Cloud &amp; network</td>
              </tr>
              <tr>
                <td><strong>Grafana / observability</strong></td>
                <td>Grafana Cloud</td>
                <td>Apps &amp; tooling</td>
              </tr>
              <tr>
                <td><strong>OAuth / SSO app</strong></td>
                <td>Google Workspace</td>
                <td>Apps &amp; tooling</td>
              </tr>
              <tr>
                <td><strong>Payments</strong></td>
                <td>Stripe</td>
                <td>Apps &amp; tooling</td>
              </tr>
              <tr>
                <td><strong>Other</strong></td>
                <td>None named</td>
                <td>Apps &amp; tooling</td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            An engineer does not need to request a GitLab repository. Engineers get the Developer role on
            GitLab repositories by default.
          </p>
        </aside>

        <h2 className="doc-h2" id="how-long-the-access-lasts">How long the access lasts</h2>
        <p className="doc-p">
          The grant is valid until the task&rsquo;s <strong>due date</strong> (shown as{' '}
          <strong>Access until</strong>). An empty date means the access is permanent. Ask for a date when the
          work has an end. The date can be changed later from the task page by the requester, an assignee or
          root. The dashboard does not remove access when the date passes; a person removes it in the target
          system.
        </p>

        <h2 className="doc-h2" id="what-happens-after-you-create-it">What happens after you create it</h2>
        <p className="doc-p">
          Your task stays <strong>pending</strong> until an assignee decides it or you cancel it. The
          dashboard assigns the people, records each step and sends the notifications. Every system gets the
          same flow: there are no review layers, no escalation, no maintainer sign-off and no risk level.
        </p>
        <DocSequence
          caption="From creating the task to a decision"
          participants={[
            { id: 'You', label: 'You', kind: 'person' },
            { id: 'As', label: 'Assignee', kind: 'person' },
            { id: 'Dash', label: 'Dashboard' },
            { id: 'Slack', label: 'Slack' },
            { id: 'Sys', label: 'Target system' },
          ]}
          steps={[
            { from: 'You', to: 'Dash', text: 'Create the access task' },
            { from: 'Dash', to: 'Dash', text: 'Assign the default access assignees and anyone you added' },
            { from: 'Dash', to: 'As', text: 'Email every assignee' },
            { from: 'Dash', to: 'Slack', text: 'Post the new task' },
            { kind: 'note', over: ['Sys'], text: 'A person grants the access here by hand' },
            { from: 'As', to: 'Dash', text: 'Approve, with a note' },
            { kind: 'note', over: ['As', 'Dash'], text: 'Or deny, also with a note' },
            { from: 'Dash', to: 'Slack', text: 'Post the decision' },
            { from: 'Dash', to: 'You', text: 'Email the decision' },
          ]}
        />
        <p className="doc-p">
          An assignee may ask you a question before deciding. The question arrives as a comment on the task.
          Answer it there. If someone else should decide, add them as an assignee.
        </p>
        <p className="doc-p">
          The email names the new status and the person who set it. Open the task to read the note. The
          Notification center on your <Link to="/settings/profile">Account</Link> page can send the same news
          as a browser notification. It is off until you turn it on.
        </p>
        <p className="doc-p">
          The dashboard saves each step before it sends the email and the Slack post, and it sends them only
          where they are set up. A message that fails does not undo the step.
        </p>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Approved means an assignee recorded the grant. A person applies the permission in the target
            system. The dashboard changes nothing there.
          </p>
        </aside>
        <p className="doc-p">
          A decision also closes the task. <strong>Approved</strong> closes it as done,{' '}
          <strong>denied</strong> as not planned, <strong>cancelled</strong> as cancelled. A task whose access
          was decided cannot be reopened; create a new one.
        </p>

        <h2 className="doc-h2" id="who-decides">Who decides</h2>
        <ul className="doc-list">
          <li>
            <strong>Any assignee</strong> can approve or deny, whatever their role. A new member who is assigned may
            decide.
          </li>
          <li><strong>Root and maintainers</strong> can approve or deny any access task.</li>
          <li>
            <strong>The requester cannot</strong> decide their own task. Root is the one exception, and the
            timeline and Slack mark that decision as self-decided.
          </li>
          <li>
            A <strong>note is required</strong> with every decision. Root and maintainers can correct it
            afterwards.
          </li>
        </ul>
        <p className="doc-p">
          You know who your approvers are: they are the assignees listed on your task.
        </p>
        <p className="doc-p">
          The <strong>default access assignees</strong> are a flat list that root and maintainers keep on{' '}
          <Link to="/settings/reviewers">Settings → Access assignees</Link>. Every new access task gets all of
          them, and you cannot skip that by leaving the field empty. Today the list is two people. It is read when
          this page opens:
        </p>
        <ReviewerRoster />
        <p className="doc-p">
          You can add any person with a staff account on top of the defaults, whatever their role. You cannot add
          yourself. The form shows the defaults as chips: remove one and that person is not assigned to this task.
        </p>
        <p className="doc-p">
          After the task exists, you can add or remove assignees on your own access task from its page, and so can
          any assignee and root. A new member can do this on the task they created and on no one else&rsquo;s.
        </p>

        <h2 className="doc-h2" id="follow-your-task">Follow your task</h2>
        <p className="doc-p">
          <Link to="/tasks">Tasks</Link> lists every task. Filter with <code>requester:me</code> to see yours,
          or <code>access:pending</code> for the ones still waiting. Open a task to see its status, its
          description and its timeline.
        </p>
        <ul className="doc-list">
          <li>
            <strong>Comment.</strong> Write in the box under the timeline. Type <code>@</code> to mention a
            teammate, who then gets an email. Use a mention when the task is urgent. You can edit or delete
            your own comments.
          </li>
          <li>
            <strong>Edit the reason.</strong> You can rewrite the reason of a task you created. Only the
            requester can; assignees and root cannot.
          </li>
          <li>
            <strong>Cancel.</strong> While the access is pending, press <strong>Close…</strong> in the
            sidebar&rsquo;s State section and pick <strong>Cancelled</strong>. The task stays in the list as
            cancelled and keeps its timeline. Root can cancel anyone&rsquo;s.
          </li>
        </ul>
        <aside className="doc-note">
          <span className="doc-note-label">Note</span>
          <p>
            Every signed-in staff member can open any access task and comment on it. Keep passwords, tokens
            and keys out of the reason and the comments.
          </p>
        </aside>

        <h2 className="doc-h2" id="from-an-ai-agent">From an AI agent</h2>
        <p className="doc-p">
          The infras MCP server has six access-request tools. An agent connected with your token creates and
          comments as you.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Tool</th>
                <th>What it does</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>create_access_request</code></td>
                <td>Creates an access task with the same system, target and reason as the form.</td>
              </tr>
              <tr>
                <td><code>list_access_requests</code></td>
                <td>Lists the access tasks you created, or every access task.</td>
              </tr>
              <tr>
                <td><code>get_access_request</code></td>
                <td>Reads one access task with its timeline.</td>
              </tr>
              <tr>
                <td><code>comment_access_request</code></td>
                <td>Posts a comment, such as a recommendation for the assignee.</td>
              </tr>
              <tr>
                <td><code>edit_access_request</code></td>
                <td>Rewrites the reason of a task you created.</td>
              </tr>
              <tr>
                <td><code>cancel_access_request</code></td>
                <td>Withdraws a pending task you created.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          No tool approves or denies. A person does that in the dashboard.{' '}
          <Link to="/docs/mcp">MCP tools</Link> lists every tool with its parameters.
        </p>
        <p className="doc-p">
          <code>create_access_request</code> takes <code>durationKind</code> and <code>durationDays</code>{' '}
          instead of a date: <code>temporary</code> with <code>7</code> sets the due date seven days from now,
          and the default is <code>permanent</code>. The default access assignees are always assigned;{' '}
          <code>extraReviewerEmails</code> adds more people on top of them, of any role but not you. Its{' '}
          <code>maintainerEmail</code> input is ignored.
        </p>
        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Ready to ask?</h2>
            <p>
              Name the exact resource and say what you are doing with it, so an assignee does not have to ask.
            </p>
          </div>
          <Link className="btn" to="/tasks/new?template=access">
            Create an access task
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
